"use client";

import { ArrowUpRight, Play } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { GuideConcept } from "@/content/guide-schema";

export type GuideVideoCopy = {
  openVideo: string;
  playVideo: string;
  video: string;
};

function youtubeVideoId(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, "");
  let id: string | null = null;

  if (host === "youtu.be") {
    id = parsed.pathname.slice(1);
  } else if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "youtube-nocookie.com"
  ) {
    if (parsed.pathname === "/watch") {
      id = parsed.searchParams.get("v");
    } else if (
      parsed.pathname.startsWith("/embed/") ||
      parsed.pathname.startsWith("/shorts/")
    ) {
      id = parsed.pathname.split("/")[2] ?? null;
    }
  }

  return id && /^[\w-]{6,}$/.test(id) ? id : null;
}

function proxiedThumbnail(id: string) {
  const source = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  return `/api/material-preview/image?src=${encodeURIComponent(source)}`;
}

export function GuideVideo({
  copy,
  video,
}: {
  copy: GuideVideoCopy;
  video: NonNullable<GuideConcept["video"]>;
}) {
  const videoId = youtubeVideoId(video.url);
  const [playing, setPlaying] = useState(false);

  return (
    <section className="grid gap-3 rounded-2xl border bg-card p-4">
      {videoId ? (
        <div className="relative aspect-video overflow-hidden rounded-xl bg-foreground">
          {playing ? (
            <iframe
              className="absolute inset-0 size-full"
              src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size={null}
              onClick={() => setPlaying(true)}
              aria-label={copy.playVideo}
              className="interaction-focus group absolute inset-0 grid size-full rounded-none border-0 p-0 hover:bg-transparent active:translate-y-0"
            >
              {/* biome-ignore lint/performance/noImgElement: dynamic same-origin proxy URL with a query string. */}
              <img
                src={proxiedThumbnail(videoId)}
                alt=""
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-foreground/20 transition-colors group-hover:bg-foreground/10" />
              <span className="relative grid size-14 place-items-center rounded-full bg-background/90 text-foreground shadow-lg transition-transform group-hover:scale-105">
                <Play
                  className="size-6 translate-x-0.5 fill-current"
                  aria-hidden
                />
              </span>
            </Button>
          )}
        </div>
      ) : null}

      <div>
        <span className="font-semibold text-primary text-xs uppercase tracking-wide">
          {copy.video}
        </span>
        <p className="mt-1 font-heading font-bold text-base leading-tight">
          {video.title}
        </p>
        {video.description ? (
          <p className="mt-1.5 text-muted-foreground text-sm leading-6">
            {video.description}
          </p>
        ) : null}
        <Link
          href={video.url}
          target="_blank"
          rel="noreferrer"
          className="interaction-focus group mt-2 inline-flex items-center gap-1.5 font-medium text-primary text-sm"
        >
          {copy.openVideo}
          <ArrowUpRight
            className="size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </div>
    </section>
  );
}
